import { useEffect, useRef, useCallback, useState } from 'react';
import * as PIXI from 'pixi.js';
import { TokenLayer } from './TokenLayer';
import { TorchlightOverlay } from './TorchlightOverlay';
import { MapBackgroundLayer } from './MapBackgroundLayer';
import { CampaignMapLayer } from './CampaignMapLayer';
import { PingEffectLayer } from './PingEffectLayer';
import { EffectsLayer } from './EffectsLayer';
import { DrawingLayer } from './DrawingLayer';
import { useEffectStore } from '../stores/effectStore';
import { useTokenStore } from '../stores/tokenStore';
import { useToolStore } from '../stores/toolStore';
import { useCampaignMapStore } from '../stores/campaignMapStore';
import { useCampaignStore } from '../stores/campaignStore';
import { useMapStore } from '../stores/mapStore';
import { MeasureOverlay } from '../components/MeasureOverlay';
import { DrawToolBar } from '../components/DrawToolBar';
import { useDrawingStore } from '../stores/drawingStore';
import { PinDetailPanel } from '../components/PinDetailPanel';
import { Icon } from '../components/Icon';
import { useDMConsoleStore } from '../stores/dmConsoleStore';
import { useMapKeyboard } from '../hooks/useMapKeyboard';
import { useIsGM } from '../hooks/useIsGM';
import { resetPixiTokenClicked, resetPixiEffectClicked } from './pixiTokenState';
import { TokenRenderer } from './TokenRenderer';
import { audioEngine } from '../audio/audioEngine';
import { supabase } from '../lib/supabase';

interface MapCanvasProps {
  gridSize?: number;
  gridWidth?: number;
  gridHeight?: number;
  onTokenSelect?: (tokenId: string | null) => void;
}

/**
 * Pixi.js-based map canvas
 * Renders the game board with grid and tokens
 */
export function MapCanvas({
  gridSize = 50,
  gridWidth = 20,
  gridHeight = 20,
  // onTokenSelect - reserved for future use
}: MapCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const tokenLayerRef = useRef<TokenLayer | null>(null);
  const torchlightRef = useRef<TorchlightOverlay | null>(null);
  const mapBackgroundRef = useRef<MapBackgroundLayer | null>(null);
  const campaignMapLayerRef = useRef<CampaignMapLayer | null>(null);
  const pingLayerRef = useRef<PingEffectLayer | null>(null);
  const effectsLayerRef = useRef<EffectsLayer | null>(null);
  const drawingLayerRef = useRef<DrawingLayer | null>(null);
  const pingChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const gridRef = useRef<PIXI.Graphics | null>(null);

  // GM tray state — controls snap above it
  const trayOpen = useDMConsoleStore((s) => s.isOpen);
  const controlsBottom = trayOpen ? 'calc(40vh + 16px)' : '60px';

  // Drawing tool state
  const isDrawingRef = useRef(false);

  // Pan state for right-click drag
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

  // Fog preview state (GM sees map as players do)
  const [playerPreview, setPlayerPreview] = useState(false);
  // Edit mode state (GM can see all labels and drag pins)
  const [editMode, setEditMode] = useState(false);

  // Zoom state (from mapStore for external control)
  const zoom = useMapStore((s) => s.zoom);
  const setZoom = useMapStore((s) => s.setZoom);
  const activeMap = useMapStore((s) => s.activeMap);
  const MIN_ZOOM = 0.25;
  const MAX_ZOOM = 4;
  const ZOOM_STEP = 0.1;

  // Store actions for external access
  const addToken = useTokenStore((s) => s.addToken);
  const clearSelection = useTokenStore((s) => s.clearSelection);
  const activeTool = useToolStore((s) => s.activeTool);

  const mapMode = useCampaignMapStore((s) => s.mapMode);
  const isEditingPins = useCampaignMapStore((s) => s.isEditingPins);
  const addPin = useCampaignMapStore((s) => s.addPin);
  const pins = useCampaignMapStore((s) => s.pins);
  const pinVersion = useCampaignMapStore((s) => s.pinVersion);
  const activeCampaignMap = useCampaignMapStore((s) => s.activeCampaignMap);
  const loadCampaignMap = useCampaignMapStore((s) => s.loadCampaignMap);
  const setMapMode = useCampaignMapStore((s) => s.setMapMode);
  const selectedPinId = useCampaignMapStore((s) => s.selectedPinId);
  const partyLocation = useCampaignMapStore((s) => s.partyLocation);
  const currentCampaignId = useCampaignStore((s) => s.currentCampaignId);
  const isGM = useIsGM();

  // GM gets persistent token name labels; players get hover-only.
  useEffect(() => {
    TokenRenderer.setAlwaysShowNames(isGM);
  }, [isGM]);

  // Enable keyboard shortcuts (Delete, Ctrl+Z, Ctrl+Y)
  useMapKeyboard(isGM);

  // Auto-load campaign map when entering a campaign
  // Clear stale map when campaign changes, then load the new one
  const prevCampaignRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentCampaignId && currentCampaignId !== prevCampaignRef.current) {
      // Campaign changed — reset old map state and load fresh
      if (prevCampaignRef.current && activeCampaignMap) {
        useCampaignMapStore.getState().setCampaignMap(null);
      }
      prevCampaignRef.current = currentCampaignId;
      loadCampaignMap(currentCampaignId);
    }
  }, [currentCampaignId, loadCampaignMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle adding token at position
  const addTokenAtPosition = useCallback(
    (clientX: number, clientY: number) => {
      if (!canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      // Convert screen → world coords (account for pan + zoom)
      const worldX = (clientX - rect.left - panOffset.x) / zoom;
      const worldY = (clientY - rect.top - panOffset.y) / zoom;

      // Snap position to grid in world space
      const snappedX = Math.floor(worldX / gridSize) * gridSize;
      const snappedY = Math.floor(worldY / gridSize) * gridSize;

      addToken({
        name: `Token ${Date.now().toString(36).slice(-4)}`,
        position: { x: snappedX, y: snappedY },
      });
    },
    [addToken, gridSize, panOffset, zoom]
  );

  // Handle ping at a canvas position — local effect + broadcast
  const doPing = useCallback((worldX: number, worldY: number, broadcast = true) => {
    const layer = pingLayerRef.current;
    if (layer) {
      layer.ping(worldX, worldY);
    } else {
      console.warn('[MapCanvas] doPing: pingLayerRef is null — visual ping skipped');
    }
    audioEngine.playSynthPing();

    if (broadcast && pingChannelRef.current) {
      pingChannelRef.current.send({
        type: 'broadcast',
        event: 'ping',
        payload: { x: worldX, y: worldY },
      });
    }
  }, []);

  // Handle right-click to start panning
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Right-click starts panning
    if (e.button === 2) {
      isPanningRef.current = true;
      panStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        offsetX: panOffset.x,
        offsetY: panOffset.y,
      };
      e.preventDefault();
      return;
    }

    // Left-click with draw tool starts a stroke
    if (e.button === 0 && activeTool === 'draw' && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const worldX = (e.clientX - rect.left - panOffset.x) / zoom;
      const worldY = (e.clientY - rect.top - panOffset.y) / zoom;
      useDrawingStore.getState().startStroke(worldX, worldY);
      isDrawingRef.current = true;
      e.preventDefault();
    }
  }, [panOffset, activeTool, zoom]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isPanningRef.current) {
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      setPanOffset({
        x: panStartRef.current.offsetX + dx,
        y: panStartRef.current.offsetY + dy,
      });
      return;
    }

    if (isDrawingRef.current && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const worldX = (e.clientX - rect.left - panOffset.x) / zoom;
      const worldY = (e.clientY - rect.top - panOffset.y) / zoom;
      useDrawingStore.getState().addPoint(worldX, worldY);
    }
  }, [panOffset, zoom]);

  const handleMouseUp = useCallback(() => {
    isPanningRef.current = false;
    if (isDrawingRef.current) {
      useDrawingStore.getState().endStroke();
      isDrawingRef.current = false;
    }
  }, []);

  // Handle scroll wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();

    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom + delta)));
  }, [zoom, setZoom]);

  // Zoom control functions
  const zoomIn = useCallback(() => {
    setZoom(Math.min(MAX_ZOOM, zoom + ZOOM_STEP));
  }, [zoom, setZoom]);

  const zoomOut = useCallback(() => {
    setZoom(Math.max(MIN_ZOOM, zoom - ZOOM_STEP));
  }, [zoom, setZoom]);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
    try { localStorage.removeItem('machina:zoom'); } catch { /* ignore */ }
  }, [setZoom]);

  // Handle left-click based on active tool
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Check if a Pixi element was just clicked (Pixi event fires before React)
      // These guards prevent the canvas click from undoing the Pixi selection
      if (resetPixiTokenClicked()) return;
      if (resetPixiEffectClicked()) return;

      // Only handle clicks on the canvas itself (not on overlay UI buttons)
      const target = e.target as HTMLElement;
      if (target.tagName === 'BUTTON' || target.closest('button')) return;

      // Draw tool handles input via mousedown/move/up, not click
      if (activeTool === 'draw') return;

      if (activeTool === 'ping') {
        // Convert client coords to world coords (account for pan + zoom)
        const rect = canvasRef.current!.getBoundingClientRect();
        const worldX = (e.clientX - rect.left - panOffset.x) / zoom;
        const worldY = (e.clientY - rect.top - panOffset.y) / zoom;
        doPing(worldX, worldY);
      } else if (activeTool === 'spell') {
        // Place a spell area effect at click location
        const rect = canvasRef.current!.getBoundingClientRect();
        const worldX = (e.clientX - rect.left - panOffset.x) / zoom;
        const worldY = (e.clientY - rect.top - panOffset.y) / zoom;
        // Snap to grid center
        const snappedX = Math.round(worldX / gridSize) * gridSize;
        const snappedY = Math.round(worldY / gridSize) * gridSize;

        const store = useEffectStore.getState();
        // Use the current spell config from the toolbar (defaults to fire circle 4 squares)
        const spellConfig = (window as unknown as { __spellConfig?: { effect: string; shape: string; radius: number } }).__spellConfig || { effect: 'fire', shape: 'circle', radius: 4 };
        store.addEffect({
          id: crypto.randomUUID(),
          name: `${spellConfig.effect} area`,
          origin: { x: snappedX, y: snappedY },
          radiusSquares: spellConfig.radius,
          shape: spellConfig.shape as 'circle' | 'cone' | 'line' | 'square',
          effect: spellConfig.effect as import('@machina/core').EffectName,
          intensity: 'normal',
          alpha: 0.6,
          createdAt: Date.now(),
        });
      } else if (activeTool === 'token') {
        addTokenAtPosition(e.clientX, e.clientY);
      } else if (activeTool === 'select') {
        clearSelection();
        // Also deselect any selected spell effect
        useEffectStore.getState().selectEffect(null);
      }
    },
    [activeTool, addTokenAtPosition, clearSelection, doPing, gridSize, panOffset, zoom]
  );

  useEffect(() => {
    if (!canvasRef.current) return;

    // Get container dimensions
    const container = canvasRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Create Pixi application
    const app = new PIXI.Application({
      width,
      height,
      backgroundColor: 0x1a1410,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    });

    container.appendChild(app.view as HTMLCanvasElement);
    appRef.current = app;

    // Make stage interactive for click-to-deselect
    // Note: Token clicks use stopPropagation, so this only fires for empty space clicks
    app.stage.eventMode = 'static';
    app.stage.hitArea = app.screen;
    app.stage.sortableChildren = true; // respect zIndex on child containers

    // Create map background layer (renders below everything) - for battle maps
    const mapBackground = new MapBackgroundLayer(gridSize / 5); // gridSize/5 = pixels per foot (50px/5ft = 10px/ft)
    mapBackgroundRef.current = mapBackground;
    app.stage.addChild(mapBackground.container);

    // Create campaign map layer (renders in place of battle map when in campaign mode)
    const campaignMapLayer = new CampaignMapLayer();
    campaignMapLayerRef.current = campaignMapLayer;
    campaignMapLayer.setMapDimensions(width, height);
    campaignMapLayer.onPinClick = (pinId) => {
      console.log('[MapCanvas] Pin clicked:', pinId);
      useCampaignMapStore.getState().selectPin(pinId);
    };
    campaignMapLayer.onPinMoved = (pinId, x, y) => {
      useCampaignMapStore.getState().updatePin({ pinId, x, y });
    };
    campaignMapLayer.onPinToggleVisibility = (pinId, visible) => {
      useCampaignMapStore.getState().setPinState(pinId, visible ? 'discovered' : 'hidden');
    };
    campaignMapLayer.onPartyMarkerMoved = (x, y) => {
      useCampaignMapStore.getState().setPartyLocation(null, x, y);
    };
    app.stage.addChild(campaignMapLayer.container);

    // Create grid graphics
    const grid = new PIXI.Graphics();
    gridRef.current = grid;

    // Draw grid lines
    grid.lineStyle(1, 0x3a342a, 0.5);

    // Vertical lines
    for (let x = 0; x <= gridWidth; x++) {
      grid.moveTo(x * gridSize, 0);
      grid.lineTo(x * gridSize, gridHeight * gridSize);
    }

    // Horizontal lines
    for (let y = 0; y <= gridHeight; y++) {
      grid.moveTo(0, y * gridSize);
      grid.lineTo(gridWidth * gridSize, y * gridSize);
    }

    app.stage.addChild(grid);

    // Create drawing layer (above grid, below tokens)
    const drawingLayer = new DrawingLayer();
    drawingLayerRef.current = drawingLayer;
    app.stage.addChild(drawingLayer.container);

    // Torchlight overlay — auto-activates when any token has emitsLight
    const torchlight = new TorchlightOverlay(gridSize, gridWidth, gridHeight);
    torchlightRef.current = torchlight;
    torchlight.setTicker(app.ticker);
    torchlight.container.eventMode = 'none';
    torchlight.container.interactiveChildren = false;

    // Spell effects layer — sits UNDER tokens so mobs standing in an AoE
    // remain clickable/draggable. Effect edges outside a token can still be
    // clicked for selection.
    const effectsLayer = new EffectsLayer(gridSize);
    effectsLayerRef.current = effectsLayer;
    effectsLayer.setTicker(app.ticker);
    effectsLayer.container.zIndex = 5;
    app.stage.addChild(effectsLayer.container);

    // Token layer — above effects.
    const tokenLayer = new TokenLayer(gridSize);
    tokenLayerRef.current = tokenLayer;
    tokenLayer.container.zIndex = 10;
    app.stage.addChild(tokenLayer.container);

    // Torchlight overlay — above effects, below pings
    app.stage.addChild(torchlight.container);

    // Create ping effect layer (above everything)
    const pingLayer = new PingEffectLayer();
    pingLayerRef.current = pingLayer;
    pingLayer.setTicker(app.ticker);
    app.stage.addChild(pingLayer.container);

    // Set initial layer visibility based on current map mode
    const currentMode = useCampaignMapStore.getState().mapMode;
    const isBattle = currentMode === 'battle';
    const isCampaign = currentMode === 'campaign';
    mapBackground.container.visible = isBattle;
    campaignMapLayer.container.visible = isCampaign;
    grid.visible = isBattle;
    tokenLayer.container.visible = isBattle;
    drawingLayer.container.visible = isBattle;
    torchlight.container.visible = isBattle;

    // Handle resize
    const handleResize = () => {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      app.renderer.resize(newWidth, newHeight);
      // Update campaign map dimensions on resize
      campaignMapLayer.setMapDimensions(newWidth, newHeight);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      mapBackground.destroy();
      mapBackgroundRef.current = null;
      campaignMapLayer.destroy();
      campaignMapLayerRef.current = null;
      effectsLayer.destroy();
      effectsLayerRef.current = null;
      drawingLayer.destroy();
      drawingLayerRef.current = null;
      pingLayer.destroy();
      pingLayerRef.current = null;
      torchlight.destroy();
      torchlightRef.current = null;
      tokenLayer.destroy();
      tokenLayerRef.current = null;
      gridRef.current = null;
      app.destroy(true, { children: true });
    };
  }, [gridSize, gridWidth, gridHeight]);

  // Apply pan offset to stage
  useEffect(() => {
    if (appRef.current) {
      appRef.current.stage.position.set(panOffset.x, panOffset.y);
    }
  }, [panOffset]);

  // Apply zoom to stage
  useEffect(() => {
    if (appRef.current) {
      appRef.current.stage.scale.set(zoom, zoom);
    }
  }, [zoom]);

  // Refresh battle map background when activeMap changes (e.g. session loading)
  useEffect(() => {
    if (mapBackgroundRef.current && activeMap) {
      mapBackgroundRef.current.refresh();
    }
  }, [activeMap]);

  // Refresh campaign map layer when campaign map data loads (async from store)
  useEffect(() => {
    if (campaignMapLayerRef.current && activeCampaignMap) {
      campaignMapLayerRef.current.refresh();
    }
  }, [activeCampaignMap]);

  // Refresh campaign map when pins change (fog redraws based on revealed pins)
  // Uses pinVersion counter for reliable change detection (Map objects in deps can be flaky)
  useEffect(() => {
    if (campaignMapLayerRef.current && pins.size > 0) {
      campaignMapLayerRef.current.refresh();
    }
  }, [pinVersion, pins]);

  // Toggle layer visibility based on map mode
  useEffect(() => {
    const isBattle = mapMode === 'battle';
    const isCampaign = mapMode === 'campaign';
    // In iso mode, hide all top-down layers — IsoMapCanvas renders separately
    console.log('[MapCanvas] Map mode changed:', mapMode);

    // Toggle battle map layers
    if (mapBackgroundRef.current) {
      mapBackgroundRef.current.container.visible = isBattle;
      if (isBattle) mapBackgroundRef.current.refresh();
    }
    if (gridRef.current) {
      gridRef.current.visible = isBattle;
    }
    if (tokenLayerRef.current) {
      tokenLayerRef.current.container.visible = isBattle;
    }
    if (effectsLayerRef.current) {
      effectsLayerRef.current.container.visible = isBattle;
    }
    if (drawingLayerRef.current) {
      drawingLayerRef.current.container.visible = isBattle;
    }
    if (torchlightRef.current) {
      torchlightRef.current.container.visible = isBattle;
    }

    // Toggle campaign map layer
    if (campaignMapLayerRef.current) {
      campaignMapLayerRef.current.container.visible = isCampaign;
    }
  }, [mapMode]);

  // Pass isGM to campaign map layer
  useEffect(() => {
    if (campaignMapLayerRef.current) {
      campaignMapLayerRef.current.setGMMode(isGM);
    }
  }, [isGM]);

  // Pass player preview to campaign map layer
  useEffect(() => {
    if (campaignMapLayerRef.current) {
      campaignMapLayerRef.current.setPlayerPreview(playerPreview);
    }
  }, [playerPreview]);

  // Pass edit mode to campaign map layer
  useEffect(() => {
    if (campaignMapLayerRef.current) {
      campaignMapLayerRef.current.setEditMode(editMode);
    }
  }, [editMode]);

  // Pass selected pin to campaign map layer for highlight
  useEffect(() => {
    if (campaignMapLayerRef.current) {
      campaignMapLayerRef.current.selectPin(selectedPinId);
    }
  }, [selectedPinId]);

  // Re-render campaign map when party location changes (e.g. travel, /pin, drag)
  useEffect(() => {
    if (campaignMapLayerRef.current && partyLocation) {
      campaignMapLayerRef.current.refresh();
    }
  }, [partyLocation]);

  // Subscribe to ping broadcasts from other players
  useEffect(() => {
    if (!currentCampaignId) return;

    const channel = supabase
      .channel(`ping-${currentCampaignId}`)
      .on('broadcast', { event: 'ping' }, (msg) => {
        const { x, y } = msg.payload as { x: number; y: number };
        // Play locally without re-broadcasting
        pingLayerRef.current?.ping(x, y);
        audioEngine.playSynthPing();
      })
      .subscribe();

    pingChannelRef.current = channel;

    return () => {
      pingChannelRef.current = null;
      supabase.removeChannel(channel);
    };
  }, [currentCampaignId]);

  // Subscribe to spell effect broadcasts
  useEffect(() => {
    if (!currentCampaignId) return;

    useEffectStore.getState().subscribe(currentCampaignId);

    return () => {
      useEffectStore.getState().unsubscribe();
    };
  }, [currentCampaignId]);

  // Update campaign map click handler for pin placement
  useEffect(() => {
    if (campaignMapLayerRef.current) {
      campaignMapLayerRef.current.onMapClick = async (x, y) => {
        // Only create pins when in editing mode and we have a campaign map
        if (isEditingPins && activeCampaignMap) {
          const pinName = prompt('Enter pin name:', `Location ${Date.now().toString(36).slice(-4)}`);
          if (pinName) {
            await addPin({
              campaignMapId: activeCampaignMap.id,
              x,
              y,
              name: pinName,
              iconType: 'landmark',
              state: 'hidden',
              tags: [],
            });
          }
        }
      };
    }
  }, [isEditingPins, activeCampaignMap, addPin]);

  // Determine cursor based on active tool or panning
  const getCursor = () => {
    if (isPanningRef.current) return 'grabbing';
    // In campaign mode with pin editing, show crosshair
    if (mapMode === 'campaign' && isEditingPins) return 'crosshair';
    switch (activeTool) {
      case 'token':
        return 'crosshair';
      case 'measure':
        return 'crosshair';
      case 'draw':
        return 'crosshair';
      case 'ping':
        return 'crosshair';
      default:
        return 'default';
    }
  };

  return (
    <div
      ref={canvasRef}
      data-tour="map-canvas"
      style={{
        width: '100%',
        height: '100%',
        cursor: getCursor(),
        position: 'relative',
      }}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
    >
      {/* Map mode toggle - always visible */}
      <div
          style={{
            position: 'absolute',
            bottom: `calc(${controlsBottom} + 114px)`,
            right: 16,
            display: 'flex',
            gap: 2,
            zIndex: 100,
            backgroundColor: 'rgba(10, 8, 12, 0.9)',
            borderRadius: 6,
            padding: 2,
            border: '1px solid var(--color-border)',
          }}
        >
          <button
            onClick={() => setMapMode('campaign')}
            title="Campaign Map"
            style={{
              width: 32,
              height: 32,
              borderRadius: 4,
              border: 'none',
              backgroundColor: mapMode === 'campaign' ? 'rgba(212, 175, 55, 0.25)' : 'transparent',
              color: mapMode === 'campaign' ? '#d4af37' : 'var(--color-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="map" size={16} />
          </button>
          <button
            onClick={() => setMapMode('battle')}
            title="Battle Map"
            style={{
              width: 32,
              height: 32,
              borderRadius: 4,
              border: 'none',
              backgroundColor: mapMode === 'battle' ? 'rgba(212, 175, 55, 0.25)' : 'transparent',
              color: mapMode === 'battle' ? '#d4af37' : 'var(--color-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="sword" size={16} />
          </button>
          <button
            onClick={() => setMapMode('iso')}
            title="Isometric View"
            style={{
              width: 32,
              height: 32,
              borderRadius: 4,
              border: 'none',
              backgroundColor: mapMode === 'iso' ? 'rgba(212, 175, 55, 0.25)' : 'transparent',
              color: mapMode === 'iso' ? '#d4af37' : 'var(--color-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="diamond" size={16} />
          </button>
        </div>

      {/* GM tools: edit mode + fog preview + force sync */}
      {isGM && mapMode === 'campaign' && (
        <div
          style={{
            position: 'absolute',
            bottom: `calc(${controlsBottom} + 154px)`,
            right: 16,
            display: 'flex',
            gap: 2,
            zIndex: 100,
            backgroundColor: 'rgba(10, 8, 12, 0.9)',
            borderRadius: 6,
            padding: 2,
            border: '1px solid var(--color-border)',
          }}
        >
          <button
            onClick={() => setEditMode(!editMode)}
            title={editMode ? 'Exit Edit Mode' : 'Edit Pins'}
            style={{
              width: 32,
              height: 32,
              borderRadius: 4,
              border: 'none',
              backgroundColor: editMode ? 'rgba(212, 175, 55, 0.25)' : 'transparent',
              color: editMode ? '#d4af37' : 'var(--color-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="edit" size={16} />
          </button>
          <button
            onClick={() => setPlayerPreview(!playerPreview)}
            title={playerPreview ? 'Viewing as Players — Click for GM View' : 'Viewing as GM — Click for Player View'}
            style={{
              width: 32,
              height: 32,
              borderRadius: 4,
              border: 'none',
              backgroundColor: playerPreview ? 'rgba(245, 158, 11, 0.2)' : 'transparent',
              color: playerPreview ? '#f59e0b' : 'var(--color-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name={playerPreview ? 'eye' : 'eye-off'} size={16} />
          </button>
          <button
            onClick={() => {
              // Force sync by toggling map mode twice - this broadcasts to all players
              setMapMode('battle');
              setTimeout(() => setMapMode('campaign'), 100);
            }}
            title="Force Sync Map to All Players"
            style={{
              width: 32,
              height: 32,
              borderRadius: 4,
              border: 'none',
              backgroundColor: 'transparent',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="refresh-cw" size={16} />
          </button>
        </div>
      )}

      {/* Zoom controls */}
      <div
        style={{
          position: 'absolute',
          bottom: controlsBottom,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          zIndex: 100,
        }}
      >
        <button
          onClick={zoomIn}
          title="Zoom In"
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)',
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            fontWeight: 'bold',
          }}
        >
          +
        </button>
        <button
          onClick={resetZoom}
          title="Reset to 100%"
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--radius-sm)',
            border: `1px solid ${Math.abs(zoom - 1) > 0.01 ? '#d4af37' : 'var(--color-border)'}`,
            backgroundColor: Math.abs(zoom - 1) > 0.01 ? 'rgba(212, 175, 55, 0.15)' : 'var(--color-surface)',
            color: Math.abs(zoom - 1) > 0.01 ? '#d4af37' : 'var(--color-text-secondary)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          onClick={zoomOut}
          title="Zoom Out"
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)',
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            fontWeight: 'bold',
          }}
        >
          −
        </button>
      </div>

      {/* Measure tool overlay */}
      <MeasureOverlay containerRef={canvasRef} />

      {/* Draw tool bar */}
      <DrawToolBar />

      {/* Pin detail panel (campaign map mode) */}
      {mapMode === 'campaign' && <PinDetailPanel />}
    </div>
  );
}
